"use client";

/**
 * Reveal 컴포넌트
 * @description 화면에 들어올 때 한 번 떠오른다(아래에서 위로, 투명에서 불투명으로). 시안에
 *   없는 웹의 장식이라 여기 산다. 랜딩의 다른 구간과 같은 GSAP · ScrollTrigger 를 쓴다.
 *
 *   움직임을 줄여 달라는 설정(`prefers-reduced-motion`)에서는 아무것도 하지 않는다 — 그때
 *   상자는 처음부터 그 자리에 그대로 서 있다. 애니메이션 전 상태를 CSS 로 미리 숨기지
 *   않는 것도 같은 이유다: 스크립트가 못 돌면 내용이 영영 안 보인다.
 * @example
 * <Reveal delay={index * REVEAL_STAGGER}><ContentCard … /></Reveal>
 */
import { memo, useRef } from "react";

import type { RevealProps } from "./Reveal.types";

import { gsap, useGSAP } from "@/shared/lib/gsap";

/** 줄 안에서 이웃과 벌어지는 시간(초) */
export const REVEAL_STAGGER = 0.06;
/** 떠오르는 거리 — `space.gap.xl`(24) 와 같은 값. 토큰은 CSS 라 JS 에 옮겨 적는다 */
const RISE_PX = 24;
const DURATION_S = 0.7;
/** 상자의 위가 뷰포트 아래 10% 에 닿을 때 — 보이기 시작하자마자 움직인다 */
const START = "top 92%";

const Reveal = memo<RevealProps>(
  ({ children, delay = 0, className, testID }) => {
    const ref = useRef<HTMLDivElement>(null);

    useGSAP(
      () => {
        const el = ref.current;
        if (!el) return;
        const mm = gsap.matchMedia();
        mm.add("(prefers-reduced-motion: no-preference)", () => {
          gsap.fromTo(
            el,
            { y: RISE_PX, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              delay,
              duration: DURATION_S,
              ease: "power3.out",
              clearProps: "transform,opacity",
              scrollTrigger: { trigger: el, start: START, once: true },
            },
          );
        });
        return () => mm.revert();
      },
      { scope: ref, dependencies: [delay] },
    );

    return (
      <div ref={ref} className={className} data-testid={testID}>
        {children}
      </div>
    );
  },
);

Reveal.displayName = "Reveal";

export default Reveal;
